/**
 * Invite User Modal
 * Phase 5: RBAC - Adım 1.4
 * 
 * Modal for inviting users by email
 * Converted from InviteUserPage.jsx to modal component
 */

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { createInvite, clearActionError } from '../../redux/slices/inviteSlice';
import alertify from 'alertifyjs';

/**
 * InviteUserModal Component
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Close handler
 */
const InviteUserModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { actionLoading, actionError } = useSelector((state) => state.invite);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Clear form when modal closes
  useEffect(() => {
    if (!open) {
      setEmail('');
      setEmailError('');
      dispatch(clearActionError());
    }
  }, [open, dispatch]);

  // Email validation
  const validateEmail = (value) => {
    if (!value.trim()) {
      return 'Email adresi gereklidir';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Geçerli bir email adresi giriniz';
    }
    
    return '';
  };

  // Handle email change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear errors
    setEmailError('');
    dispatch(clearActionError());
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    // Dispatch create invite
    try {
      await dispatch(createInvite({ email: email.trim() })).unwrap();
      
      // Success
      alertify.success(`Davet başarıyla gönderildi: ${email}`);
      
      // Close modal and reset form
      onClose();
      setEmail('');
      setEmailError('');
    } catch (err) {
      // Error handled by Redux and displayed in Alert
      console.error('Invite error:', err);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!actionLoading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Kullanıcı Davet Et
          </Typography>
          <IconButton 
            onClick={handleClose} 
            disabled={actionLoading}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Şirketinize yeni kullanıcı davet edin. Kullanıcı email adresine davet linki gönderilecek.
          </Typography>

          {/* Error Message */}
          {actionError && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }} 
              onClose={() => dispatch(clearActionError())}
            >
              {actionError}
            </Alert>
          )}

          {/* Email Input */}
          <TextField
            fullWidth
            label="Email Adresi"
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError || 'Davet etmek istediğiniz kullanıcının email adresini giriniz'}
            placeholder="ornek@email.com"
            disabled={actionLoading}
            required
            autoFocus
          />

          {/* Info Box */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Davet Süreci:
            </Typography>
            <Typography variant="body2" component="div">
              1. <strong>Yeni Kullanıcı:</strong> Email sistemde kayıtlı değilse, kullanıcı önce kayıt olacak sonra şirkete katılacak.
              <br />
              2. <strong>Mevcut Kullanıcı:</strong> Email sistemde kayıtlıysa, kullanıcı giriş yapıp daveti kabul edecek.
              <br />
              3. Davet linki <strong>24 saat</strong> geçerlidir.
            </Typography>
          </Alert>

          {/* Additional Warning */}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              ⚠️ <strong>Not:</strong> Saatte maksimum 25 davet gönderebilirsiniz. Bu limit tüm şirket için geçerlidir.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={actionLoading}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={actionLoading || !email.trim()}
          >
            {actionLoading ? 'Gönderiliyor...' : 'Davet Gönder'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteUserModal;

